import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Highlighter,
  Menu,
  MenuItem,
  Typeahead,
} from "react-bootstrap-typeahead";
import "react-bootstrap-typeahead/css/Typeahead.css";
import "react-bootstrap-typeahead/css/Typeahead.bs5.css";
import { useSelector } from "react-redux";
import { dataSelector, loadedSelector } from "../../pages/reports/data.slice";

export default function DBField({
  id,
  name,
  value,
  updateValue,
  updateDatabase,
  entity,
  databases,
  keyField,
  valueField,
  disabled = false,
  multiple,
  ...others
}) {
  const [selectedOptions, setSelectedOptions] = useState(null);
  const typeaheadRef = useRef(null);
  const data = useSelector(dataSelector);
  const loaded = useSelector(loadedSelector);

  const isMultiDatabase = databases.length > 1;
  const isLoading = databases.some(
    (database) => !loaded[`${database}.${entity}`]
  );
  const isDisabled = disabled || isLoading;

  const onChange = (items) => {
    const hasItems = items && items.length > 0;
    updateValue(
      hasItems ? (multiple ? items.map((i) => i.id) : items[0].id) : null
    );
    if (isMultiDatabase) {
      updateDatabase(hasItems ? items[0].database : null);
    }
    setSelectedOptions(hasItems ? items : null);
    if (multiple) typeaheadRef.current?.toggleMenu();
  };

  const onBlur = () => {
    if (!selectedOptions) typeaheadRef.current?.clear();
  };

  const options = useMemo(
    () =>
      databases.flatMap((database) =>
        (data[`${database}.${entity}`] || [])
          .filter((item) => String(item[keyField] ?? "").trim() !== "")
          .map((item) => {
            const label =
              typeof valueField === "function"
                ? valueField(item)
                : item[valueField];
            return { id: item[keyField], database, label };
          })
      ),
    [data, databases, entity, keyField, valueField]
  );

  useEffect(() => {
    if (!value) setSelectedOptions(null);
  }, [value]);

  const renderGroupedMenu = (
    results,
    {
      newSelectionPrefix,
      paginationText,
      renderMenuItemChildren,
      ...menuProps
    },
    state
  ) => (
    <Menu {...menuProps}>
      {results.flatMap((option, position) => [
        ...(results[position - 1]?.database !== option.database
          ? [
              <Menu.Header key={`${option.database}-header`}>
                {option.database.toUpperCase()}
              </Menu.Header>,
            ]
          : []),
        <MenuItem
          key={`${option.database}:${option.id}`}
          option={option}
          position={position}
        >
          <Highlighter search={state.text}>{option.label}</Highlighter>
        </MenuItem>,
      ])}
    </Menu>
  );

  return (
    <Typeahead
      ref={typeaheadRef}
      id={id}
      labelKey="label"
      onChange={onChange}
      onBlur={onBlur}
      options={options}
      placeholder={isLoading ? "Loading..." : "Choose a value..."}
      selected={isDisabled || !selectedOptions ? [] : selectedOptions}
      disabled={isDisabled}
      isLoading={isLoading}
      multiple={isDisabled ? false : multiple}
      renderMenu={isMultiDatabase ? renderGroupedMenu : undefined}
      paginate={!isMultiDatabase}
      {...others}
    />
  );
}
